import { useState } from "react";
import { Send, Loader2, Mail, MapPin, Globe, Twitter, Instagram, Youtube, Facebook } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      className="relative py-24 md:py-32 lg:py-40 bg-black/[0.93]"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <div className="gold-line mb-6" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
              Get in Touch
            </span>
            <h2
              data-testid="contact-heading"
              className="font-heading text-3xl md:text-4xl font-medium text-white mt-4 leading-tight"
            >
              Ready to get
              <br />
              <span className="text-gold">EHO Ready?</span>
            </h2>
            <p className="mt-6 text-base text-zinc-400 leading-relaxed max-w-md">
              Tell us about your business and what you need. Danny and the team
              will review your enquiry and get back to you within 24 hours.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center">
                  <Mail size={16} className="text-gold" />
                </div>
                <div>
                  <p className="text-xs text-zinc-600 uppercase tracking-wider">Email</p>
                  <p className="text-sm text-zinc-300">Info@chefcomplianceuk.com</p>
                  <p className="text-sm text-zinc-300">Admin@chefcomplianceuk.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center">
                  <Globe size={16} className="text-gold" />
                </div>
                <div>
                  <p className="text-xs text-zinc-600 uppercase tracking-wider">Website</p>
                  <p className="text-sm text-zinc-300">chefcomplianceuk.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center">
                  <MapPin size={16} className="text-gold" />
                </div>
                <div>
                  <p className="text-xs text-zinc-600 uppercase tracking-wider">Location</p>
                  <p className="text-sm text-zinc-300">United Kingdom — Nationwide Service</p>
                </div>
              </div>
            </div>

            {/* Socials */}
            <div className="mt-8">
              <p className="text-xs text-zinc-600 uppercase tracking-wider mb-3">Follow Us</p>
              <div className="flex items-center gap-3">
                <a href="https://x.com/Danny496974" target="_blank" rel="noopener noreferrer" data-testid="social-x" className="w-9 h-9 rounded bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center text-zinc-400 hover:text-gold hover:border-[#c9a84c]/40 hover:drop-shadow-[0_0_8px_rgba(201,168,76,0.4)] transition-all">
                  <Twitter size={15} />
                </a>
                <a href="https://instagram.com/Chef_compliance_uk" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="w-9 h-9 rounded bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center text-zinc-400 hover:text-gold hover:border-[#c9a84c]/40 hover:drop-shadow-[0_0_8px_rgba(201,168,76,0.4)] transition-all">
                  <Instagram size={15} />
                </a>
                <a href="https://youtube.com/@ChefComplianceUK" target="_blank" rel="noopener noreferrer" data-testid="social-youtube" className="w-9 h-9 rounded bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center text-zinc-400 hover:text-gold hover:border-[#c9a84c]/40 hover:drop-shadow-[0_0_8px_rgba(201,168,76,0.4)] transition-all">
                  <Youtube size={15} />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61575398185691" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="w-9 h-9 rounded bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center text-zinc-400 hover:text-gold hover:border-[#c9a84c]/40 hover:drop-shadow-[0_0_8px_rgba(201,168,76,0.4)] transition-all">
                  <Facebook size={15} />
                </a>
              </div>
              <div className="mt-3 space-y-1">
                <p className="text-xs text-zinc-500"><span className="text-zinc-600">X:</span> <a href="https://x.com/Danny496974" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">@Danny496974</a></p>
                <p className="text-xs text-zinc-500"><span className="text-zinc-600">Instagram:</span> <a href="https://instagram.com/Chef_compliance_uk" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">@Chef_compliance_uk</a></p>
                <p className="text-xs text-zinc-500"><span className="text-zinc-600">YouTube:</span> <a href="https://youtube.com/@ChefComplianceUK" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">@ChefComplianceUK</a></p>
                <p className="text-xs text-zinc-500"><span className="text-zinc-600">Facebook:</span> <a href="https://www.facebook.com/profile.php?id=61575398185691" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">Daniel Davies</a></p>
              </div>
            </div>
          </div>

          {/* Right form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="gold-card p-8 md:p-10 space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm text-zinc-400">Full Name *</Label>
              <Input
                id="name"
                name="name"
                data-testid="contact-input-name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                className="bg-white/5 border-[#c9a84c]/15 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#c9a84c]/50 focus:border-[#c9a84c]/40 focus:bg-white/[0.07]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-zinc-400">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@business.com"
                className="bg-white/5 border-[#c9a84c]/15 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#c9a84c]/50 focus:border-[#c9a84c]/40 focus:bg-white/[0.07]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-sm text-zinc-400">Business Name</Label>
              <Input
                id="company"
                name="company"
                data-testid="contact-input-company"
                value={form.company}
                onChange={handleChange}
                placeholder="Your restaurant or food business"
                className="bg-white/5 border-[#c9a84c]/15 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#c9a84c]/50 focus:border-[#c9a84c]/40 focus:bg-white/[0.07]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-sm text-zinc-400">Message *</Label>
              <Textarea
                id="message"
                name="message"
                rows={4}
                data-testid="contact-input-message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us about your business and what you need help with..."
                className="bg-white/5 border-[#c9a84c]/15 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#c9a84c]/50 focus:border-[#c9a84c]/40 focus:bg-white/[0.07] resize-none"
              />
            </div>

            <Button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="w-full bg-gold hover:bg-[#b8963f] text-black py-3 text-sm font-semibold rounded transition-colors tracking-wide"
            >
              {sending ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              {sending ? "Sending..." : "Send Enquiry"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
